import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  Bill,
  CreateBillData,
  UpdateBillData,
  BillsResponse,
  BillResponse,
  BillCorrectionPreview,
  BillCorrectionPreviewResponse,
  GetBillsParams,
  BillsResult,
} from '../types/bill';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

const buildBillsQuery = (params?: GetBillsParams): string => {
  if (!params) return '';

  const query = new URLSearchParams();

  if (params.vendor_id != null) {
    query.set('vendor_id', String(params.vendor_id));
  }
  if (params.status != null) {
    const status = Array.isArray(params.status) ? params.status.join(',') : params.status;
    query.set('status', status);
  }
  if (params.balance_min != null) {
    query.set('balance_min', String(params.balance_min));
  }
  if (params.start_date) {
    query.set('start_date', params.start_date);
  }
  if (params.end_date) {
    query.set('end_date', params.end_date);
  }
  if (params.search) {
    query.set('search', params.search);
  }
  if (params.is_overdue != null) {
    query.set('is_overdue', params.is_overdue ? '1' : '0');
  }
  if (params.include_lines != null) {
    query.set('include_lines', params.include_lines ? '1' : '0');
  }
  if (params.include_vendor != null) {
    query.set('include_vendor', params.include_vendor ? '1' : '0');
  }
  if (params.sort) {
    query.set('sort', params.sort);
  }
  if (params.per_page != null) {
    query.set('per_page', String(params.per_page));
  }
  if (params.page != null) {
    query.set('page', String(params.page));
  }

  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

export const getBills = async (params?: GetBillsParams): Promise<BillsResult> => {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILLS.GET_ALL}${buildBillsQuery(params)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch bills');
  }

  const result: BillsResponse = await response.json();
  return {
    bills: result.data ?? [],
    meta: result.meta,
    links: result.links,
  };
};

/** Fetch every page for the given filters (use scoped filters — never unbounded). */
export const fetchAllBills = async (
  params: Omit<GetBillsParams, 'page'>
): Promise<Bill[]> => {
  const all: Bill[] = [];
  let page = 1;
  let lastPage = 1;

  do {
    const result = await getBills({ ...params, page });
    all.push(...result.bills);
    lastPage = result.meta.last_page;
    page += 1;
  } while (page <= lastPage);

  return all;
};

/**
 * Received bills with open balance for one vendor (all pages).
 * Filters `received_into_inventory` client-side — not a list API filter yet.
 */
export const getVendorPayableBills = async (vendorId: number): Promise<Bill[]> => {
  const bills = await fetchAllBills({
    vendor_id: vendorId,
    status: ['OPEN', 'PARTIAL'],
    balance_min: 0.01,
    include_lines: false,
    include_vendor: false,
    sort: 'txn_date',
    per_page: 100,
  });

  return bills.filter((bill) => bill.received_into_inventory);
};

/** Open/partial bills with outstanding balance (all pages). */
export const getOpenBills = async (
  params: Omit<GetBillsParams, 'status' | 'balance_min' | 'page'> = {}
): Promise<Bill[]> =>
  fetchAllBills({
    ...params,
    status: ['OPEN', 'PARTIAL'],
    balance_min: 0.01,
    include_lines: false,
    sort: params.sort ?? 'due_date',
    per_page: params.per_page ?? 100,
  });

/**
 * @deprecated Use `getBills` or `fetchAllBills` with explicit filters.
 * Unfiltered fetch only returns the first page and is unsafe at scale.
 */
export const getAllBills = async (): Promise<Bill[]> => {
  const result = await getBills({ per_page: 100, page: 1, include_lines: false });
  return result.bills;
};

export const getBillById = async (id: number): Promise<Bill> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILLS.GET_BY_ID}/${id}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch bill');
  }

  const result: BillResponse = await response.json();
  return result.data;
};

export const createBill = async (data: CreateBillData): Promise<Bill> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILLS.CREATE}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create bill');
  }

  const result: BillResponse = await response.json();
  return result.data;
};

export const previewBillCorrection = async (
  billId: number,
  data: UpdateBillData
): Promise<BillCorrectionPreview> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILLS.CORRECTION_PREVIEW}/${billId}/correction-preview`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to preview bill correction');
  }

  const result: BillCorrectionPreviewResponse = await response.json();
  return result.data;
};

export const updateBill = async (billId: number, data: UpdateBillData): Promise<Bill> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILLS.UPDATE}/${billId}`, {
    method: 'PATCH',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update bill');
  }

  const result: BillResponse = await response.json();
  return result.data;
};

export const deleteBill = async (billId: number): Promise<void> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILLS.DELETE}/${billId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to delete bill');
  }
};

export const receiveBill = async (billId: number): Promise<Bill> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILLS.RECEIVE}/${billId}/receive`, {
    method: 'POST',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to receive bill');
  }

  const result: BillResponse = await response.json();
  return result.data;
};

export const voidBill = async (billId: number): Promise<Bill> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BILLS.VOID}/${billId}/void`, {
    method: 'POST',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to void bill');
  }

  const result: BillResponse = await response.json();
  return result.data;
};
